<template>
    <mini-calculator-page>
        <div slot="summary-panel">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Life Expectancy Calculator" icon="life-expectancy"/>

                <control for="deceased_gender" label="Gender">
                    <select v-field-control
                            required
                            name="deceased_gender"
                            v-model="gender" ref="gender"
                            @change="calcAgeAtDeath">
                        <option value="" disabled hidden>Please select...</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                    <control-error :show="$error('deceased_gender')">
                        The deceased's gender is required
                    </control-error>
                </control>

                <control for="deceased_date_of_birth" label="Date of Birth">
                    <date-input v-model="dateOfBirth" ref="dateOfBirth"
                                v-field-control:deceased_date_of_birth.required="{type:'date'}"
                                @input="calcAgeAtDeath"></date-input>
                    <control-error :show="$error('deceased_date_of_birth')">
                        The date of birth is required and must be in the format DD/MM/YYYY
                    </control-error>
                    <control-error :show="!validDob()">
                        The date of birth must be within 100 years of today
                    </control-error>
                </control>

                <control for="date_of_assumed_settlement" label="Date of Assumed Settlement">
                    <date-input v-model="dateOfAssumedSettlement" ref="dateOfAssumedSettlement"
                                v-field-control:date_of_assumed_settlement.required="{type:'date'}"
                                @input="calcAgeAtDeath"></date-input>
                    <control-error :show="$error('date_of_assumed_settlement')">
                        The date of assumed settlement is required and must be in the format DD/MM/YYYY
                    </control-error>
                    <control-error :show="!validDoAS()">
                        The date of assumed settlement must be within 100 years of today
                    </control-error>
                </control>

                <control :label="'Life Expectancy Type'" for="method1">
                    <select id="method1" v-model="lifeExpectancyMethod" ref="lifeExpectancyMethod" @change="calcAgeAtDeath">
                        <option value="" disabled hidden>Please select...</option>
                        <option value="ogden_table">Ogden Table 1/2</option>
                        <option value="years_and_months">Enter Years/Months</option>
                        <option value="enter_date">Enter Date</option>
                        <option value="age_at_death">Enter Age At Death (years)</option>
                        <option value="percentage_of_normal_life_expectancy">% of Normal Life Expectancy</option>
                        <option value="percentage_reduction_in_normal_life_expectancy">
                            % Reduction in Normal Life Expectancy
                        </option>
                    </select>
                </control>

                <control label="Enter Years/Months From Today" v-if="lifeExpectancyMethod=='years_and_months'">
                    <div class="a-form-inputs--half">
                        <div class="a-form-inputs__container">
                            <select id="years1" v-model.number="years" ref="years" @change="calcAgeAtDeath">
                                <option v-for="year in 76" :value="year - 1">
                                    {{ year - 1}} year{{year - 1 === 1 ? '' : 's'}}
                                </option>
                            </select>
                        </div>
                        <div class="a-form-inputs__container">
                            <select id="months1" v-model.number="months" ref="months" @change="calcAgeAtDeath">
                                <option v-for="month in 12" :value="month - 1">
                                    {{ month - 1 }} month{{month - 1 === 1 ? '' : 's'}}
                                </option>
                            </select>
                        </div>
                    </div>
                </control>

                <control label="Percentage of Normal Life Expectancy" for="percentage_of_normal_life_expectancy"
                         v-if="lifeExpectancyMethod=='percentage_of_normal_life_expectancy'"
                         before-after="percent">
                    <input autocomplete="off" v-model.number="percentageOfNormalLifeExpectancy"
                           v-field-control
                           v-two-dp
                           required
                           max="100"
                           min="1"
                           :disabled="lifeExpectancyMethod!=='percentage_of_normal_life_expectancy'"
                           ref="percentageOfNormalLifeExpectancy"
                           @input="calcAgeAtDeath"
                           name="percentage_of_normal_life_expectancy"
                           type="number">
                    <control-error :show="$error('percentage_of_normal_life_expectancy')">
                        The percentage of normal life expectancy must be number between 1 and 100
                    </control-error>
                </control>

                <control label="Percentage Reduction in Normal Life Expectancy"
                         for="percentage_reduction_in_normal_life_expectancy"
                         v-if="lifeExpectancyMethod=='percentage_reduction_in_normal_life_expectancy'"
                         before-after="percent">
                    <input autocomplete="off" v-model.number="percentageReductionInNormalLifeExpectancy"
                           :disabled="lifeExpectancyMethod!=='percentage_reduction_in_normal_life_expectancy'"
                           v-field-control
                           required
                           max="100"
                           min="1"
                           ref="percentageReductionInNormalLifeExpectancy"
                           @input="calcAgeAtDeath"
                           name="percentage_reduction_in_normal_life_expectancy"
                           type="number">
                    <control-error :show="$error('percentage_reduction_in_normal_life_expectancy')">
                        The percentage reduction in normal life expectancy must be number between 1 and 100
                    </control-error>
                </control>

                <control label="Expected End of Life Date" for="date_of_death" v-if="lifeExpectancyMethod==='enter_date'">
                    <date-input v-model="dateOfDeathIsAssumed"
                                ref="dateOfDeathIsAssumed"
                                @input="calcAgeAtDeath"></date-input>
                </control>

                <control label="Expected Age at End of Life (Years)" for="date_of_death"
                         v-if="lifeExpectancyMethod==='age_at_death'"
                         before-after="years-old">
                    <input autocomplete="off" v-model.number="ageAtDeath"
                           ref="ageAtDeath"
                           v-field-control
                           v-two-dp
                           min="20"
                           @input="calcAgeAtDeath"
                           name="life_age_at_death"
                           type="number">
                </control>

                <h2 class="u-r-margin-top-40">
                    Result
                </h2>

                <control label="Expected Age at End of Life (Years)" for="life_age_at_death"
                         v-if="lifeExpectancyMethod!=='age_at_death'"
                         :modal-name="'life_age_at_death_modal'"
                         before-after="years-old-on-dark">
                    <input autocomplete="off" v-model.number="ageAtDeath"
                           v-field-control
                           v-two-dp
                           min="20"
                           disabled
                           @input="calcAgeAtDeath"
                           name="life_age_at_death"
                           type="number">
                </control>

                <control label="Expected End of Life Date" for="date_of_death"
                         v-show="lifeExpectancyMethod!=='enter_date'">
                    <input autocomplete="off" v-model="dateOfDeath" disabled>
                </control>

                <control label="Age at today's date" for="age_today">
                    <input autocomplete="off" :value="this.ageToday()" disabled>
                </control>

            </div>
        </div>
        <div slot="results-panel" class="flex-grid--vertical" v-show="showResults">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto">
                <h2>
                    Explanation
                </h2>

                <div v-if="lifeExpectancyMethod == 'ogden_table' || lifeExpectancyMethod == 'percentage_of_normal_life_expectancy' || lifeExpectancyMethod == 'percentage_reduction_in_normal_life_expectancy'">

                    <p v-if="lifeExpectancyMethod == 'ogden_table'">
                        Life expectancy has been calculated using gender and current age from the 0% discount column of Ogden Table 1 (for males) and Ogden Table 2 (for females)
                    </p>

                    <div class="a-table--really-wide" v-if="lifeExpectancyMethod == 'ogden_table'">
                        <table class="a-table a-table--miniCalc" cellspacing="0" cellpadding="0" v-if="tableData">
                            <thead>
                            <tr v-if="tableData.table.thead[0].length">
                                <th :colspan="tableData.table.thead[1].length">
                                    <div class="u-text-center">
                                        {{ tableData.table.thead[0] }}
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                <th v-for="column in tableData.table.thead[1]">
                                    {{ column }}
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="row in tableData.table.tbody">
                                <td v-for="column in row" :class="{ 'a-table--highlight' : column.highlight }">
                                    {{ column.value }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="tableData">
                        <h2>Calculation</h2>
                            <div v-if="Object.keys(tableData.table.tbody).length > 1">
                                <p>
                                    The person's age at today's date is {{ ageToday() }} years old (DOB {{ dateOfBirth }}).
                                    Therefore, interpolation between rows {{ formulaData()[0] }} and {{ formulaData()[2] }} is
                                    required on the 0% column to receive the projected mortality value:
                                </p>

                                <div class="m-formula">
                                    <div class="m-formula__item--row">
                                        {{ formulaData()[3] }} + (({{ formulaData()[2] }} - {{ ageToday() }}) x ({{ formulaData()[1] }} - {{ formulaData()[3] }})) = {{ result() }}
                                    </div>
                                </div>

                            </div>
                        <p>
                            Life expectancy is {{ result() }} years giving age at end of life of {{ roundValue(ageAtDeath ,2) }} years old.
                        </p>
                    </div>
                </div>

                <div v-if="lifeExpectancyMethod == 'years_and_months'">
                    <p>
                        The person's age at todays date is {{ this.ageToday() }} years old (DOB {{ dateOfBirth }}).
                    </p>

                    <p>
                        Based on the person being expected to live for another {{ years }} year(s) and {{ months
                        }} month(s) they would be expected to live until the age of {{ ageAtDeath }} years old.
                    </p>

                    <value-bars :values="[ageAtDeath,this.ageToday()]">
                        <h3>Age at Expected Date of Death &mdash; {{ dateOfDeath }}</h3>
                        <div class="m-bars__bar bar1"><span></span></div>
                        <h3>Age at Today's Date &mdash; {{ this.dateToday() }}</h3>
                        <div class="m-bars__bar bar2"><span></span></div>
                    </value-bars>
                </div>

                <div v-if="lifeExpectancyMethod == 'enter_date'">
                    <p>
                        The person's age at todays date is {{ this.ageToday() }} years old (DOB {{ dateOfBirth }}).
                    </p>

                    <p>
                        Based on the person being expected to live until the date of {{ dateOfDeathIsAssumed
                        }} they would be expected to live until the age of {{ ageAtDeath }} years old.
                    </p>

                    <value-bars :values="[ageAtDeath, this.ageToday()]">
                        <h3>Age at Expected Date of Death &mdash; {{ dateOfDeath }}</h3>
                        <div class="m-bars__bar bar1"><span></span></div>
                        <h3>Age at Today's Date &mdash; {{ this.dateToday() }}</h3>
                        <div class="m-bars__bar bar2"><span></span></div>
                    </value-bars>
                </div>

                <div v-if="lifeExpectancyMethod == 'age_at_death'">
                    <p>
                        The person's age at todays date is {{ this.ageToday() }} years old (DOB {{ dateOfBirth }}).
                    </p>

                    <p>
                        Based on the person being expected to live until the age of {{ ageAtDeath
                        }} they would be expected to live until the date of {{ dateOfDeathIsAssumed }}.
                    </p>

                    <value-bars :values="[ageAtDeath, this.ageToday()]">
                        <h3>Age at Expected Date of Death &mdash; {{ dateOfDeath }}</h3>
                        <div class="m-bars__bar bar1"><span></span></div>
                        <h3>Age at Today's Date &mdash; {{ this.dateToday() }}</h3>
                        <div class="m-bars__bar bar2"><span></span></div>
                    </value-bars>
                </div>

                <div v-if="lifeExpectancyMethod == 'percentage_of_normal_life_expectancy'">
                    <p>
                        Based on gender and current age of {{ this.ageToday() }}, normal life expectancy using the 0% rate of return in Tables 1/2 of the Ogden Tables is {{ ageAtDeath }}
                    </p>

                    <p>
                        {{ percentageOfNormalLifeExpectancy }}% of normal life expectancy would result in {{ dateOfDeathIsAssumed }} years remaining or an end of life date of {{ dateOfDeath }}
                    </p>

                    <div class="a-table--really-wide">
                        <table class="a-table a-table--modal" cellspacing="0" cellpadding="0" v-if="tableData">
                            <thead>
                            <tr v-if="tableData.table.thead[0].length">
                                <th :colspan="tableData.table.thead[1].length">
                                    <div class="u-text-center">
                                        {{ tableData.table.thead[0] }}
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                <th v-for="column in tableData.table.thead[1]">
                                    {{ column }}
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="row in tableData.table.tbody">
                                <td v-for="column in row" :class="{ 'a-table--highlight' : column.highlight }">
                                    {{ column.value }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="lifeExpectancyMethod == 'percentage_reduction_in_normal_life_expectancy'">
                    <p>
                        Based on gender and current age of {{ this.ageToday() }}, reduction in normal life expectancy using the 0% rate of return in Tables 1/2 of the Ogden Tables is {{ ageAtDeath }}
                    </p>

                    <p>
                        {{ percentageReductionInNormalLifeExpectancy }}% reduction of normal life expectancy would result in {{ dateOfDeathIsAssumed }} years remaining or an end of life date of {{ dateOfDeath }}
                    </p>

                    <div class="a-table--really-wide">
                        <table class="a-table a-table--modal" cellspacing="0" cellpadding="0" v-if="tableData">
                            <thead>
                            <tr v-if="tableData.table.thead[0].length">
                                <th :colspan="tableData.table.thead[1].length">
                                    <div class="u-text-center">
                                        {{ tableData.table.thead[0] }}
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                <th v-for="column in tableData.table.thead[1]">
                                    {{ column }}
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="row in tableData.table.tbody">
                                <td v-for="column in row" :class="{ 'a-table--highlight' : column.highlight }">
                                    {{ column.value }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import Vue from 'vue';
    import VueSlider from 'vue-slider-component';
    import _ from 'lodash';
    import moment from 'moment';
    import {mapGetters, mapActions} from 'vuex';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage';
    import miniCalcIcons from 'components/Icons.vue';
    import DateInput from 'components/form/control/DateInput.vue';
    import TheLifeExpectancy from 'components/LifeExpectancy.vue';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';
    import ValueBars from 'components/form/ValueBars';
    import roundValue from 'mixins/roundValue';

    export default {
        name: 'life-expectancy',
        created() {
            this.resetForm();
        },
        watch: {
            ['percentageReductionInNormalLifeExpectancy'] () {
                this.calcAgeAtDeath();
            },
            ['percentageOfNormalLifeExpectancy'] () {
                this.calcAgeAtDeath();
            },
        },
        data: function() {
            return {
                showResults: false
            }
        },
        computed: {
            ...mapGetters('mini/lifeExpectancy', {
                getAssumedSettlement: 'getAssumedSettlement',
                thePerson: 'thePerson',
                theResults: 'theResults'
            }),
            ...mapGetters('mini/age', {detailValue: 'value'}),
            dateOfBirth: {
                get() {
                    return this.thePerson.dateOfBirth;
                },
                set(value) {
                    this.updatePerson({key: 'dateOfBirth', value});
                }
            },
            gender: {
                get() {
                    return this.thePerson.gender;
                },
                set(value) {
                    this.updatePerson({key: 'gender', value});
                }
            },
            lifeExpectancyMethod: {
                get() {
                    return this.thePerson.method;
                },
                set(value) {
                    this.updatePerson({key: 'method', value});
                }
            },
            dateOfDeathIsAssumed: {
                get() {
                    return this.thePerson.dateOfDeathIsAssumed;
                },
                set(value) {
                    this.updatePerson({key: 'dateOfDeathIsAssumed', value});
                }
            },
            dateOfDeath: {
                get() {
                    return this.thePerson.dateOfDeath;
                },
                set(value) {
                    this.updatePerson({key: 'dateOfDeath', value});
                }
            },
            ageAtDeath: {
                get() {
                    return this.thePerson.ageAtDeath;
                },
                set(value) {
                    this.updatePerson({key: 'ageAtDeath', value});
                }
            },
            years: {
                get() {
                    return this.thePerson.yearsAndMonths.years;
                },
                set(value) {
                    this.updateYearsAndMonths({key: 'years', value});
                }
            },
            months: {
                get() {
                    return this.thePerson.yearsAndMonths.months;
                },
                set(value) {
                    this.updateYearsAndMonths({key: 'months', value});
                }
            },
            percentageOfNormalLifeExpectancy: {
                get() {
                    return this.thePerson.percentageOfNormalLifeExpectancy;
                },
                set(value) {
                    this.updatePerson({key: 'percentageOfNormalLifeExpectancy', value});
                }
            },
            percentageReductionInNormalLifeExpectancy: {
                get() {
                    return this.thePerson.percentageReductionInNormalLifeExpectancy;
                },
                set(value) {
                    this.updatePerson({key: 'percentageReductionInNormalLifeExpectancy', value});
                }
            },
            dateOfAssumedSettlement: {
                get() {
                    return this.getAssumedSettlement;
                },
                set(value) {
                    this.updateAssumedSettlement(value);
                }
            },
            intro: {
                get() {
                    return this.theResults.intro;
                },
            },
            explanation: {
                get() {
                    return this.theResults.explanation;
                },
            },
            outcome: {
                get() {
                    return this.theResults.outcome;
                },
            },
            formula: {
                get() {
                    return this.theResults.formula;
                },
            },
            tableData: {
                get() {
                    return this.theResults.tableData;
                },
            },
            dateOfDeathRules() {
                return "min:" + moment(this.assumedSettlement, 'DD/MM/YYYY').subtract(9, 'years').format('DD/MM/YYYY') + "|"
                    + "max:" + this.assumedSettlement;
            }
        },
        mixins: [
            FormValidator,
            CalculatorKeyBindings,
            roundValue
        ],
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        components: {
            Control,
            ControlError,
            DateInput,
            TheLifeExpectancy,
            Intro,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            miniCalcIcons,
            VueSlider,
            ValueBars
        },
        methods: {
            ...mapActions('mini/lifeExpectancy', {
                updatePerson: 'updatePerson',
                updateYearsAndMonths: 'updateYearsAndMonths',
                resetForm: 'reset',
                updateLifeExpectancy: 'updateLifeExpectancy',
                updateAssumedSettlement: 'updateAssumedSettlement'
            }),
            resetTheForm() {
                this.resetForm();
            },
            updateCounter(input) {
                this.counter = input
            },
            calcAgeAtDeath() {
                if (this.validDob() && this.validDoAS()) {
                    this.updateLifeExpectancy();
                    this.handler();
                }
            },
            handler: function () {
                if (this.$valid() && !!this.lifeExpectancyMethod) {
                    this.hidePrompt();
                    this.showResults = true;
                }
            },
            ageToday() {
                let ageToday = Math.round(moment().diff(moment(this.dateOfBirth, "DD/MM/YYYY"), 'days') / 365.25 * 100) / 100;

                if (!isNaN(ageToday)) {
                    return ageToday
                }

                return "";
            },
            dateToday() {
                return moment().format('DD/MM/YYYY');
            },
            validDob() {
                if (!this.dateOfBirth) {
                    return true;
                }

                let mDateOfBirth = moment(this.dateOfBirth, 'DD/MM/YYYY');
                if (!mDateOfBirth.isValid()) {
                    return true;
                }

                if (mDateOfBirth.isSameOrAfter(moment()) || mDateOfBirth.isSameOrBefore(moment().subtract(100, 'years'))) {
                    return false;
                }

                return true;
            },
            validDoAS() {
                if (!this.dateOfAssumedSettlement) {
                    return true;
                }

                let mDateOfAssumedSettlement = moment(this.dateOfAssumedSettlement, 'DD/MM/YYYY');
                if (!mDateOfAssumedSettlement.isValid()) {
                    return true;
                }

                if (mDateOfAssumedSettlement.isBefore(moment(this.dateOfBirth, 'DD/MM/YYYY'))) {
                    return false;
                }

                return true;
            },
            formulaData() {
                let thead = this.tableData.table.thead[1];
                let tbody = this.tableData.table.tbody;
                let data = [];
                let columnCount = 0;

                _.forEach(thead, function(value, key) {
                    if (value === "0.00") {
                        columnCount = key;
                    }
                });

                _.forEach(tbody, function(value, key) {
                    data.push(value[0].value);
                    data.push(value[columnCount].value);
                });

                return data;
            },
            result() {
                return this.roundValue(this.ageAtDeath - this.ageToday() ,2);
            }
        }
    }
</script>



// WEBPACK FOOTER //
// lifeExpectancy.vue?16bdebd0